import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase/firestore';
import {
  WordUsageDocument,
  WordUsageDisplay,
  ConceptMapData,
  ConceptMapNode,
  ConceptMapEdge,
} from '../types/wordUsage';

/**
 * Helper function to convert Firebase Timestamp or any timestamp to a Date object
 */
function getDateFromTimestamp(timestamp: any): Date {
  if (!timestamp) return new Date();

  // Handle Firebase Timestamp objects (which have toDate method)
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  // Handle Date objects
  else if (timestamp instanceof Date) {
    return timestamp;
  }
  // Handle string or number timestamps
  else {
    return new Date(timestamp || Date.now());
  }
}

/**
 * Get all word usage data
 * @returns Promise<WordUsageDocument[]>
 */
export async function getAllWordUsage(): Promise<WordUsageDocument[]> {
  try {
    const wordUsageRef = collection(db, 'word_usage');
    const wordUsageSnapshot = await getDocs(wordUsageRef);

    return wordUsageSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        timestamp: getDateFromTimestamp(data.timestamp),
      } as WordUsageDocument;
    });
  } catch (error) {
    console.error('Error getting word usage:', error);
    return [];
  }
}

/**
 * Get top word usage data
 * @param count Number of words to return
 * @returns Promise<WordUsageDisplay[]>
 */
export async function getTopWordUsage(count: number = 10): Promise<WordUsageDisplay[]> {
  try {
    const wordUsageRef = collection(db, 'word_usage');
    const wordUsageQuery = query(wordUsageRef, orderBy('count', 'desc'), limit(count));
    const wordUsageSnapshot = await getDocs(wordUsageQuery);

    return wordUsageSnapshot.docs.map((doc) => {
      const data = doc.data() as WordUsageDocument;

      // Determine forge types
      const forgeTypes: string[] = [];
      if (data.itemForgeCount && data.itemForgeCount > 0) forgeTypes.push('Item');
      if (data.locationForgeCount && data.locationForgeCount > 0) forgeTypes.push('Location');
      if (data.characterForgeCount && data.characterForgeCount > 0) forgeTypes.push('Character');
      if (data.deconstructorOutputCount && data.deconstructorOutputCount > 0)
        forgeTypes.push('Deconstructor');

      return {
        word: data.word,
        totalCount: data.count || 0,
        inputCount: data.inputCount || 0,
        outputCount: data.outputCount || 0,
        deconstructorCount: data.deconstructorOutputCount || 0,
        itemCount: data.itemForgeCount || 0,
        locationCount: data.locationForgeCount || 0,
        characterCount: data.characterForgeCount || 0,
        forgeTypes: forgeTypes,
        lastUsed: getDateFromTimestamp(data.timestamp),
      } as WordUsageDisplay;
    });
  } catch (error) {
    console.error('Error getting top word usage:', error);
    return [];
  }
}

/**
 * Get concept map data
 * @returns Promise<ConceptMapData>
 */
export async function getConceptMapData(): Promise<ConceptMapData> {
  try {
    const wordUsageData = await getAllWordUsage();

    // Create nodes and edges maps
    const nodeMap = new Map<string, ConceptMapNode>();
    const edgeMap = new Map<string, ConceptMapEdge>();

    // Process each word
    for (const word of wordUsageData) {
      const wordKey = word.word.toLowerCase();

      // Create or update node
      if (!nodeMap.has(wordKey)) {
        nodeMap.set(wordKey, {
          id: wordKey,
          label: word.word,
          count: word.count || 0,
          connectionCount: 0,
          forgeTypes: [],
        });
      }

      // Add forge types
      const node = nodeMap.get(wordKey)!;
      if (word.itemForgeCount && word.itemForgeCount > 0 && !node.forgeTypes.includes('Item')) {
        node.forgeTypes.push('Item');
      }
      if (
        word.locationForgeCount &&
        word.locationForgeCount > 0 &&
        !node.forgeTypes.includes('Location')
      ) {
        node.forgeTypes.push('Location');
      }
      if (
        word.characterForgeCount &&
        word.characterForgeCount > 0 &&
        !node.forgeTypes.includes('Character')
      ) {
        node.forgeTypes.push('Character');
      }
      if (
        word.deconstructorOutputCount &&
        word.deconstructorOutputCount > 0 &&
        !node.forgeTypes.includes('Deconstructor')
      ) {
        node.forgeTypes.push('Deconstructor');
      }

      // Process input to output connections
      if (word.inputToOutputs) {
        Object.entries(word.inputToOutputs).forEach(([outputWord, connection]) => {
          // Skip if connection is null or undefined
          if (!connection || typeof connection !== 'object') {
            return;
          }

          const targetWordKey = outputWord.toLowerCase();

          // Create target node if it doesn't exist
          if (!nodeMap.has(targetWordKey)) {
            nodeMap.set(targetWordKey, {
              id: targetWordKey,
              label: outputWord,
              count: 0,
              connectionCount: 0,
              forgeTypes: [],
            });
          }

          // Create edge key
          const edgeKey = `${wordKey}|${targetWordKey}`;

          // Create or update edge
          if (!edgeMap.has(edgeKey)) {
            // Determine forge type for the edge
            let edgeForgeType = 'Item'; // Default
            if (connection.forgeTypes) {
              if (connection.forgeTypes.Location && connection.forgeTypes.Location > 0) {
                edgeForgeType = 'Location';
              } else if (connection.forgeTypes.Character && connection.forgeTypes.Character > 0) {
                edgeForgeType = 'Character';
              }
            }

            edgeMap.set(edgeKey, {
              source: wordKey,
              target: targetWordKey,
              value: connection.count || 1,
              forgeType: edgeForgeType,
            });

            // Increment connection count for both nodes
            nodeMap.get(wordKey)!.connectionCount += 1;
            nodeMap.get(targetWordKey)!.connectionCount += 1;
          } else {
            // Update edge value
            edgeMap.get(edgeKey)!.value += connection.count || 1;
          }
        });
      }

      // Process output from input connections
      if (word.outputFromInputs) {
        Object.entries(word.outputFromInputs).forEach(([inputWord, connection]) => {
          // Skip if connection is null or undefined
          if (!connection || typeof connection !== 'object') {
            return;
          }

          const sourceWordKey = inputWord.toLowerCase();

          // Create source node if it doesn't exist
          if (!nodeMap.has(sourceWordKey)) {
            nodeMap.set(sourceWordKey, {
              id: sourceWordKey,
              label: inputWord,
              count: 0,
              connectionCount: 0,
              forgeTypes: [],
            });
          }

          // Create edge key
          const edgeKey = `${sourceWordKey}|${wordKey}`;

          // Create or update edge
          if (!edgeMap.has(edgeKey)) {
            // Determine forge type for the edge
            let edgeForgeType = 'Item'; // Default
            if (connection.forgeTypes) {
              if (connection.forgeTypes.Deconstructor && connection.forgeTypes.Deconstructor > 0) {
                edgeForgeType = 'Deconstructor';
              } else if (connection.forgeTypes.Location && connection.forgeTypes.Location > 0) {
                edgeForgeType = 'Location';
              } else if (connection.forgeTypes.Character && connection.forgeTypes.Character > 0) {
                edgeForgeType = 'Character';
              }
            }

            edgeMap.set(edgeKey, {
              source: sourceWordKey,
              target: wordKey,
              value: connection.count || 1,
              forgeType: edgeForgeType,
            });

            // Increment connection count for both nodes
            nodeMap.get(sourceWordKey)!.connectionCount += 1;
            nodeMap.get(wordKey)!.connectionCount += 1;
          } else {
            // Update edge value
            edgeMap.get(edgeKey)!.value += connection.count || 1;
          }
        });
      }
    }

    // Get all nodes as an array
    const nodes = Array.from(nodeMap.values());

    // Calculate min and max connection counts for relative heat map
    const connectionCounts = nodes.map((node) => node.connectionCount);
    const minConnections = Math.min(...connectionCounts.filter((count) => count > 0), 1);
    const maxConnections = Math.max(...connectionCounts, 1);

    // Define starting nodes that should be gold
    const startingNodes = ['water', 'fire', 'earth', 'air', 'dirt'];

    // Apply colors based on relative connection counts
    nodes.forEach((node) => {
      // Check if this is a starting node
      if (startingNodes.includes(node.id.toLowerCase())) {
        node.color = '#FFD700'; // Gold color for starting nodes
      } else if (node.connectionCount > 0) {
        // Calculate relative position in the range
        const relativePosition =
          (node.connectionCount - minConnections) / (maxConnections - minConnections || 1);

        // Create a color scale from blue (few) to purple (medium) to red (many)
        if (relativePosition < 0.33) {
          // Blue to purple gradient for lower third
          const blueComponent = Math.round(59 + (128 - 59) * (1 - relativePosition * 3));
          node.color = `rgb(${blueComponent}, 130, 246)`;
        } else if (relativePosition < 0.66) {
          // Purple to red gradient for middle third
          const redComponent = Math.round(128 + (220 - 128) * ((relativePosition - 0.33) * 3));
          const blueComponent = Math.round(246 - (246 - 128) * ((relativePosition - 0.33) * 3));
          node.color = `rgb(${redComponent}, 130, ${blueComponent})`;
        } else {
          // Red gradient for upper third
          const redComponent = Math.round(220 + (255 - 220) * ((relativePosition - 0.66) * 3));
          const blueComponent = Math.round(128 - (128 - 59) * ((relativePosition - 0.66) * 3));
          node.color = `rgb(${redComponent}, 59, ${blueComponent})`;
        }
      } else {
        // Default color for nodes with no connections
        node.color = '#9CA3AF'; // Gray
      }
    });

    // Convert maps to arrays
    return {
      nodes: nodes,
      edges: Array.from(edgeMap.values()),
    };
  } catch (error) {
    console.error('Error getting concept map data:', error);
    return { nodes: [], edges: [] };
  }
}
